/**
 * The email CLAIM QUEUE card (§ B8, catch-all arm).
 *
 * Sits directly above the forwarding-address card, because it is the same
 * feature seen from the other side: that card hands out an address whose token
 * PROVES ownership, and this one handles the mail that arrived without it.
 *
 * The card is deliberately quiet when there is nothing to do — an empty queue
 * renders NOTHING, not an empty card. A permanent "0 emails to claim" panel on
 * everyone's settings page would train travellers to skip past this region, and
 * this is the one region on the page where we need them actually reading.
 *
 * ⚠️ EVERY string in a row is attacker-controlled. Anyone on the internet can
 * send mail to the catch-all with a `From:` matching a real user, so subject,
 * sender and parsed values are hostile input. Two rules, no exceptions:
 *   - Text only: every untrusted value goes through JSX as a text child, so
 *     React escapes it. Raw-HTML injection props are banned in this file.
 *   - Sanitised and truncated by `claimQueueView` before it reaches JSX, and
 *     laid out inside `min-w-0` + `truncate`/`break-words` containers so a long
 *     value cannot push the Claim / Not mine buttons off screen.
 *
 * All the judgement — especially the unconfirmed-sender wording — lives in
 * `claimQueueView.ts` and is unit-tested there. This file only renders it.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Inbox,
  Loader2,
  AlertTriangle,
  ShieldCheck,
  ShieldAlert,
  Clock,
  Check,
  X,
  FileQuestion,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { useAuthStore } from '@/lib/auth';
import {
  presentClaimQueue,
  summariseQueue,
  cleanText,
  CLAIM_WINDOW_DAYS,
  type ClaimableMessage,
} from './claimQueueView';

type ClaimAction = 'claim' | 'decline';

/** Longest server-supplied error we will echo. It is not trusted text either. */
const MAX_SERVER_ERROR = 160;

export default function ClaimQueueCard() {
  const token = useAuthStore((s) => s.token);
  const [messages, setMessages] = useState<ClaimableMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actingId, setActingId] = useState<string | null>(null);

  const authHeaders = useCallback(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const load = useCallback(
    async (opts: { quiet?: boolean } = {}) => {
      if (!opts.quiet) setLoading(true);
      try {
        const res = await fetch('/api/booking-email/claimable', { headers: authHeaders() });
        if (!res.ok) throw new Error('claimable fetch failed');
        const data = await res.json();
        setMessages(Array.isArray(data?.messages) ? data.messages : []);
        setLoadError(null);
      } catch {
        // A failed load is NOT an empty queue, and must not be silently rendered
        // as one: a traveller whose booking is sitting here unclaimed would
        // simply never learn it existed.
        setLoadError('Could not load the emails waiting to be claimed.');
      } finally {
        setLoading(false);
      }
    },
    [authHeaders]
  );

  useEffect(() => {
    load();
  }, [load]);

  const rows = useMemo(() => presentClaimQueue(messages), [messages]);

  async function act(id: string, action: ClaimAction) {
    setActingId(id);
    setActionError(null);
    try {
      const res = await fetch(
        `/api/booking-email/claimable/${encodeURIComponent(id)}/${action}`,
        { method: 'POST', headers: authHeaders() }
      );
      if (!res.ok) {
        let serverMessage = '';
        try {
          const body = await res.json();
          serverMessage = cleanText(body?.error, MAX_SERVER_ERROR);
        } catch {
          // Non-JSON error body; the fallback copy below covers it.
        }
        throw new Error(serverMessage);
      }

      // Optimistic removal: the decision is made, so the row goes immediately
      // rather than lingering for a round-trip and inviting a second click.
      setMessages((prev) => prev.filter((m) => m?.id !== id));
      // Then re-fetch, because claiming one email can change the rest of the
      // queue server-side (a claim proves the address, so siblings may resolve).
      load({ quiet: true });
    } catch (e) {
      const detail = e instanceof Error && e.message ? ` ${e.message}` : '';
      setActionError(
        action === 'claim'
          ? `Could not claim that email — nothing was added to your trips.${detail}`
          : `Could not mark that email as not yours — it is still in the queue.${detail}`
      );
    } finally {
      setActingId(null);
    }
  }

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center gap-2 py-8 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Checking for emails waiting to be claimed…
        </CardContent>
      </Card>
    );
  }

  if (loadError) {
    return (
      <Card>
        <CardContent className="py-6">
          <div className="p-3 rounded-lg text-sm bg-red-50 text-red-700 border border-red-200 flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <span>
              {loadError} If a booking you forwarded is missing, try again in a moment.
            </span>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Nothing waiting: render nothing at all. See the file header.
  if (rows.length === 0) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Inbox className="h-5 w-5" /> Is this yours?
        </CardTitle>
        <CardDescription>
          {summariseQueue(rows.length)} These came in to our shared address rather than your own
          forwarding address, so we have no way to prove who sent them — your answer is what
          decides. Unclaimed emails are deleted after {CLAIM_WINDOW_DAYS} days.
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-3">
        {actionError && (
          <div className="p-3 rounded-lg text-sm bg-red-50 text-red-700 border border-red-200 flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <span className="break-words">{actionError}</span>
          </div>
        )}

        {rows.map((row) => {
          const busy = actingId === row.id;
          return (
            <div key={row.id} className="border border-border rounded-lg p-3 space-y-2">
              {/* Header line: what it says it is, and how long it has left. */}
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-medium text-sm truncate">{row.subject}</div>
                  <div className="text-xs text-muted-foreground truncate">
                    Appears to be from {row.sender.address}
                    {row.received && <span> · arrived {row.received}</span>}
                  </div>
                </div>
                <div
                  className={`text-xs shrink-0 flex items-center gap-1 ${
                    row.expiry.urgent ? 'text-amber-600' : 'text-muted-foreground'
                  }`}
                >
                  <Clock className="h-3 w-3" />
                  {row.expiry.text}
                </div>
              </div>

              {/* The sender line. An unconfirmed sender gets the full sentence,
                  not a chip: the traveller is the check, so they need the
                  mechanism and the test, in words. Never `accent`-styled —
                  that variant is reserved for verified data (see badge.tsx). */}
              {row.sender.warning ? (
                <div className="p-2 rounded-md text-xs bg-amber-50 text-amber-800 border border-amber-200 flex items-start gap-2">
                  <ShieldAlert className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                  <span className="break-words">{row.sender.warning}</span>
                </div>
              ) : (
                <div className="text-xs text-emerald-600 flex items-center gap-1">
                  <ShieldCheck className="h-3.5 w-3.5 shrink-0" />
                  {row.sender.label} — this address really did send it. It still needs your
                  claim before we add anything.
                </div>
              )}

              {/* What we think it is, so the traveller can recognise it. */}
              <div className="text-sm flex items-start gap-2">
                {row.parsedSummary ? (
                  <span className="break-words min-w-0">{row.parsedSummary}</span>
                ) : (
                  <span className="text-muted-foreground flex items-start gap-1 min-w-0">
                    <FileQuestion className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                    <span className="break-words">{row.parsedFallback}</span>
                  </span>
                )}
              </div>

              {row.detail && (
                <div className="text-xs text-muted-foreground break-words">{row.detail}</div>
              )}

              <div className="flex items-center gap-2 pt-1">
                <Button
                  size="sm"
                  disabled={busy || !row.actionable}
                  onClick={() => act(row.id, 'claim')}
                >
                  {busy ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-1" />
                  ) : (
                    <Check className="h-4 w-4 mr-1" />
                  )}
                  Yes, that's mine
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={busy || !row.actionable}
                  onClick={() => act(row.id, 'decline')}
                >
                  <X className="h-4 w-4 mr-1" />
                  Not mine
                </Button>
                {!row.actionable && (
                  <span className="text-xs text-muted-foreground">
                    Too late to claim this one.
                  </span>
                )}
              </div>
            </div>
          );
        })}

        <p className="text-xs text-muted-foreground">
          Claiming adds the booking to your trips and tells us this address is yours. "Not mine"
          removes it and we will stop showing you mail from that sender.
        </p>
      </CardContent>
    </Card>
  );
}
